import { useState } from "react";
import { Box, Container, Typography, Grid, TextField, Button } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SectionTitle from "../components/SectionTitle";

const CONTACT_INFO = [
  { icon: PhoneIcon, label: "Téléphone", value: "05 24 42 84 86 — 06 88 89 93 96" },
  { icon: EmailIcon, label: "Email", value: "contact@luxurylaundry.ma" },
  { icon: LocationOnIcon, label: "Adresse", value: "315, Lot Al Massar, 40000 Marrakech" },
  { icon: AccessTimeIcon, label: "Disponibilité", value: "24h/24 — 7j/7" },
];

const HeroBanner = () => (
  <Box sx={{
    position: "relative", height: { xs: "35vh", md: "42vh" },
    display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", overflow: "hidden",
    background: 'url("/images/bedroom-1.jpg") center/cover no-repeat',
    "&::before": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(10,6,2,0.65) 0%, rgba(10,6,2,0.45) 50%, rgba(10,6,2,0.88) 100%)", zIndex: 1 },
  }}>
    <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, px: { xs: 2, md: 3 } }}>
      <Typography sx={{ color: "secondary.main", fontSize: "0.68rem", letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, mb: 1.5 }}>Luxury Laundry</Typography>
      <Typography variant="h1" sx={{ color: "#fff", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: { xs: "1.8rem", md: "3rem" }, mb: 2, lineHeight: 1.1 }}>Contactez-Nous</Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
        <Box sx={{ width: 5, height: 5, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
      </Box>
    </Container>
    <Box sx={{ position: "absolute", bottom: -1, left: 0, right: 0, zIndex: 3, lineHeight: 0 }}>
      <svg viewBox="0 0 1440 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 50 }}>
        <path d="M0,30 C360,55 1080,5 1440,30 L1440,50 L0,50 Z" fill="#f5f0e8" />
      </svg>
    </Box>
  </Box>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
    <Box sx={{ width: 42, height: 42, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "6px", backgroundColor: "rgba(201,168,76,0.08)" }}>
      <Icon sx={{ fontSize: 18, color: "secondary.main" }} />
    </Box>
    <Box>
      <Typography sx={{ color: "text.secondary", fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, mb: 0.3 }}>{label}</Typography>
      <Typography sx={{ color: "text.primary", fontSize: { xs: "0.85rem", md: "0.92rem" }, fontWeight: 500, lineHeight: 1.5 }}>{value}</Typography>
    </Box>
  </Box>
);

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = () => { if (form.name && form.email && form.message) setSent(true); };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px", backgroundColor: "#fff",
      "& fieldset": { borderColor: "rgba(201,168,76,0.25)" },
      "&:hover fieldset": { borderColor: "rgba(201,168,76,0.5)" },
      "&.Mui-focused fieldset": { borderColor: "secondary.main", borderWidth: "1px" },
    },
    "& .MuiInputLabel-root": { color: "text.secondary", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#c9a84c" },
  };

  return (
    <Box>
      <HeroBanner />
      <Box sx={{ position: "relative", backgroundColor: "#f5f0e8", "&::before": { content: '""', position: "absolute", inset: 0, background: 'url("/images/flowers.jpg") center/cover no-repeat', opacity: 0.03, pointerEvents: "none", zIndex: 0 } }}>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, py: { xs: 5, md: 10 }, px: { xs: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">

            {/* Form */}
            <Grid item xs={12} md={6}>
              <SectionTitle overline="Écrivez-nous" title="Envoyez un Message" align="left" />
              {sent ? (
                <Box sx={{ p: { xs: 3, md: 4 }, textAlign: "center", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "8px", backgroundColor: "rgba(201,168,76,0.06)" }}>
                  <Box sx={{ width: 6, height: 6, backgroundColor: "secondary.main", transform: "rotate(45deg)", mx: "auto", mb: 2 }} />
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: "1.2rem", color: "text.primary", mb: 1 }}>Message envoyé !</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>Nous vous répondrons dans les plus brefs délais.</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <TextField label="Nom complet" name="name" value={form.name} onChange={handleChange} fullWidth variant="outlined" sx={inputSx} />
                  <TextField label="Adresse email" name="email" value={form.email} onChange={handleChange} fullWidth variant="outlined" sx={inputSx} />
                  <TextField label="Votre message" name="message" value={form.message} onChange={handleChange} fullWidth multiline rows={5} variant="outlined" sx={inputSx} />
                  <Button onClick={handleSubmit} variant="contained" sx={{ alignSelf: { xs: "stretch", md: "flex-start" }, backgroundColor: "secondary.main", color: "#1a0f05", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.2em", px: 5, py: 1.6, borderRadius: 0, border: "1px solid transparent", transition: "all 0.3s ease", "&:hover": { backgroundColor: "transparent", borderColor: "secondary.main", color: "secondary.main", boxShadow: "none" } }}>
                    Envoyer
                  </Button>
                </Box>
              )}
            </Grid>

            {/* Info */}
            <Grid item xs={12} md={6}>
              <SectionTitle overline="Nos coordonnées" title="Nous Trouver" align="left" />
              {CONTACT_INFO.map((item) => (<InfoRow key={item.label} {...item} />))}
              <Box sx={{ my: 4, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flexGrow: 1, height: "1px", backgroundColor: "rgba(201,168,76,0.2)" }} />
                <Box sx={{ width: 5, height: 5, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
                <Box sx={{ flexGrow: 1, height: "1px", backgroundColor: "rgba(201,168,76,0.2)" }} />
              </Box>
              <Typography sx={{ color: "text.secondary", fontSize: "0.8rem", lineHeight: 1.9 }}>
                <strong style={{ color: "#1a1108" }}>Zrikem Clean SARL</strong><br />
                RC : 114719<br />
                315, Lot Al Massar, 40000 Marrakech, Maroc
              </Typography>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage;